/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreErrorRegistry - the core's error-handler registry and product-version registry, on their own so a small bundle can use them.
 *
 * WHY A FILE OF ITS OWN (2026-10-06, DocumentBuilder loader bundle). These few lines used to sit at the top of `kCoreHandler.ts`, which also
 * holds the large `kCore` / `kRegionalFormat` tables (about 47 KB minified, not tree-shakeable). A module that only needed `getCoreUtils()` -
 * `kBrowserSettingsCore`, `kBrowserLocalStorage`, `kBlobBytes` - therefore pulled the whole of it. They import THIS file now; the loader bundle of
 * DocumentBuilder (guarded to < 60 KB) can adopt them.
 *
 * `kCoreHandler.ts` RE-EXPORTS every name here, so the public API is unchanged and there is ONE singleton: `SetCoreUtils` imported from either
 * file sets the same handler that `getCoreUtils` from either file reads (the state lives in this module only).
 *
 * Provides:
 *   - ICoreUtils:       interface for injectable error handler
 *   - DefaultCoreUtils: server-safe implementation (logs to console, no UI)
 *   - SetCoreUtils():   replaces the handler at runtime (called by kalmstromShared4
 *                       to register ks, or by FunctionErrorHandler in Azure Functions)
 *   - SetCoreUtilsResolver(), getCoreUtils(), setProductVersion(), getProductVersion()
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 * What a second consumer would change: nothing.
 */

// ── Error-handler interface ───────────────────────────────────────────────────

export interface ICoreUtils {
    /** When true, methods return early to prevent cascading errors after a failure. */
    readonly KErrorMode: boolean;
    /**
     * Record an error from the given code location. Must not show UI to the user.
     * `additionalMessage` is an optional caller-supplied detail string (matches
     * ks.KGlobalErrorHandler's optional 3rd arg so existing browser code that
     * passed three arguments — for example IsoPdfGenerator.FailWith — keeps
     * type-checking after the ks → getCoreUtils migration). Implementations
     * may ignore it; the default handler appends it to the console line.
     */
    KGlobalErrorHandler(e: any, location: string, additionalMessage?: string): void;
}

// ── Default implementation ────────────────────────────────────────────────────
// Logs to the console only — no dialogs, no network calls, no user interaction.

class DefaultCoreUtils implements ICoreUtils {
    public KErrorMode = false;

    public KGlobalErrorHandler = (e: any, location: string): void => {
        let Message: string = String(e);
        if (e instanceof Error) { Message = e.message; }
        console.error(`[kCore:${location}] ${Message}`);
    };
}

// ── Module-level singleton ────────────────────────────────────────────────────

let _cu: ICoreUtils = new DefaultCoreUtils();

/**
 * Replace the active error handler.
 * Call once at startup:
 *   - Browser: kalmstromShared4 calls SetCoreUtils(ks)
 *   - Azure Function: pass a FunctionErrorHandler instance
 */
export const SetCoreUtils = (utils: ICoreUtils): void => { _cu = utils; };

/**
 * An optional AMBIENT handler resolver, consulted by getCoreUtils() before the module-level one.
 *
 * WHY. `_cu` is a process-wide singleton, which is correct in a browser (one page, one user) and
 * WRONG on a server that runs several invocations in one worker. Each invocation there installs its
 * own handler, so overlapping invocations stomp each other. Measured on Azure Functions 2026-07-28:
 *
 *   * an error raised by the StatsMonitor timer was REPORTED UNDER "OutboxMonitor", because that
 *     timer had re-installed the singleton while the first call was still in flight; and, worse,
 *   * each new handler starts with `KErrorMode = false`, so a timer starting mid-run SILENTLY CLEARED
 *     another invocation's latched error gate — the run then carried on past a failure that was
 *     supposed to stop it.
 *
 * A host that can scope a handler to one invocation (Node's AsyncLocalStorage) registers a resolver
 * here; everything else is untouched and keeps the singleton. This file stays browser-safe: the
 * resolver is a plain function supplied by the host, and no Node API is imported.
 */
let _resolve: (() => ICoreUtils | null) | null = null;

/** Install (or clear, with null) the ambient resolver. Hosts with no per-invocation scope skip it. */
export const SetCoreUtilsResolver = (resolver: (() => ICoreUtils | null) | null): void => {
    _resolve = resolver;
};

/** Read the active handler (used internally by kCoreTime and kCoreObjects). */
export const getCoreUtils = (): ICoreUtils => {
    if (_resolve !== null) {
        // A resolver that throws must never take the error handler down with it — fall back.
        try {
            const Scoped: ICoreUtils | null = _resolve();
            if (Scoped !== null && Scoped !== undefined) { return Scoped; }
        } catch (Ignored) {
            // ERROR-SUPPRESSED-JUSTIFIED: this IS the error-reporting lookup. Reporting a failure to
            // resolve the reporter would recurse; the module-level handler below is the correct and
            // complete fallback, and it is what every non-scoped host uses anyway.
        }
    }
    return _cu;
};

// ── Product-version registry ──────────────────────────────────────────────────
// Host-supplied dotted product version (e.g. "0.5.8.1"). Browser bridges it
// from ks.KApplication.version at bundle init; Azure Functions bridge it from
// FunctionEnv at startup. Lives here (not on a per-product class) so any
// cross-project core code that needs to embed the host's version in output
// (PDF metadata, log lines, audit rows) reads the same registry instead of
// reaching back into a host-specific class.

let _productVersion: string = "";

/** Replace the host-supplied product version. Call once at startup. */
export const setProductVersion = (v: string): void => { _productVersion = v; };

/** Read the host-supplied product version. "" until set by the host. */
export const getProductVersion = (): string => _productVersion;
